import React, { useState, useEffect, useRef } from 'react';
import { getAllAuthors, getAllPublishers, getMetadataBySourceType, getAllSourceTypes, getAllMetadataTypes, MetadataField, MetadataType, Publisher, SourceType, addAuthor } from '../services/adminApi';
import { INGEST_SOURCE_LABELS, VISIBILITY_LABELS } from '../types/cms';
import { useAuth } from '../context/AuthContext';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';
import '../pages/CMSDashboard.css';

interface ContentSourceModalProps {
    mode: 'create' | 'edit';
    initialData?: {
        title?: string;
        source?: string;
        source_type?: string;
        ingest_source?: any;
        filter_tags?: string[];
        visibility?: string;
        ref_visibility?: string;
        metadata?: Record<string, any>;
        sarangpur_path?: string | null;
    };
    onClose: () => void;
    onSubmit: (data: any) => void;
    error?: string | null;
    isSubmitting?: boolean;
}

// Helper function to create a stable key from initialData (moved outside to avoid dependency issues)
const getInitialDataKey = (data: ContentSourceModalProps['initialData']): string => {
    if (!data) return '';
    return JSON.stringify({
        title: data.title,
        source_type: data.source_type || data.source,
        ingest_source: data.ingest_source,
        visibility: data.visibility,
        ref_visibility: data.ref_visibility,
        sarangpur_path: data.sarangpur_path,
        // Include a hash of metadata to detect changes
        metadataHash: JSON.stringify(data.metadata || {})
    });
};

const ContentSourceModal: React.FC<ContentSourceModalProps> = ({ mode, initialData, onClose, onSubmit, error, isSubmitting = false }) => {
    const { authState } = useAuth();
    const isAdmin = authState.user?.role === 'admin' || authState.user?.role === 'superadmin';
    
    // Normalize initial data - handle both 'source' and 'source_type'
    const normalizedSourceType = initialData?.source_type || initialData?.source || 'book';
    
    const [formData, setFormData] = useState({
        title: initialData?.title || '',
        source_type: normalizedSourceType,
        ingest_source: initialData?.ingest_source || undefined,
        filter_tags: initialData?.filter_tags || [],
        visibility: initialData?.visibility || 'public',
        ref_visibility: initialData?.ref_visibility || 'public',
        metadata: initialData?.metadata || {},
        sarangpur_path: initialData?.sarangpur_path || ''
    });

    const [metadataFields, setMetadataFields] = useState<MetadataField[]>([]);
    const [allMetadataTypes, setAllMetadataTypes] = useState<MetadataType[]>([]);
    const [authors, setAuthors] = useState<string[]>([]);
    const [publishers, setPublishers] = useState<Publisher[]>([]);
    const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
    const [authorSearchTerm, setAuthorSearchTerm] = useState(initialData?.metadata?.author || '');
    const [authorDropdownOpen, setAuthorDropdownOpen] = useState(false);
    const [selectedAuthorIndex, setSelectedAuthorIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [customMetadataFields, setCustomMetadataFields] = useState<Array<{ 
        mode: 'select' | 'free';
        key: string;
        value: string;
        metadataType?: MetadataType | null;
    }>>([]);
    const [isAddingNewAuthor, setIsAddingNewAuthor] = useState(false);
    const [newTag, setNewTag] = useState('');
    
    // Track the last initialData to detect when modal opens with new data
    // Use a stable key based on the actual data values, not object reference
    const lastInitialDataRef = useRef<string>('');

    // Helper function to check if a field is compulsory or suggested
    const getFieldRequirement = (fieldName: string): 'compulsory' | 'suggested' | 'optional' => {
        const currentSourceType = sourceTypes.find(st => st.source_type === formData.source_type);
        if (!currentSourceType) return 'optional';
        
        if (currentSourceType.compulsory.includes(fieldName)) {
            return 'compulsory';
        } else if (currentSourceType.suggested.includes(fieldName)) {
            return 'suggested';
        }
        return 'optional';
    };

    // Update form data only when initialData actually changes (not just object reference)
    // This prevents overwriting user edits when parent re-renders
    useEffect(() => {
        if (mode === 'edit' && initialData) {
            const currentKey = getInitialDataKey(initialData);
            // Only update if the actual data changed (different key)
            if (currentKey !== lastInitialDataRef.current) {
                lastInitialDataRef.current = currentKey;
                const normalizedSourceType = initialData?.source_type || initialData?.source || 'book';
                setFormData({
                    title: initialData.title || '',
                    source_type: normalizedSourceType,
                    ingest_source: initialData.ingest_source || undefined,
                    filter_tags: initialData.filter_tags || [],
                    visibility: initialData.visibility || 'public',
                    ref_visibility: initialData.ref_visibility || 'public',
                    metadata: initialData.metadata || {},
                    sarangpur_path: initialData.sarangpur_path || ''
                });
                // Update author search term if author exists in metadata
                const authorValue = initialData.metadata?.author || '';
                if (authorValue) {
                    setAuthorSearchTerm(authorValue);
                }
            }
        } else if (mode === 'create') {
            // Reset the ref when switching to create mode
            lastInitialDataRef.current = '';
        }
    }, [mode, initialData]);

    // Sync authorSearchTerm with metadata when it loads (only if authorSearchTerm is empty)
    useEffect(() => {
        const authorValue = formData.metadata?.author || '';
        if (authorValue && !authorSearchTerm) {
            setAuthorSearchTerm(authorValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.metadata?.author]);

    // Load authors, publishers, source types, and all metadata types on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const [authorsData, publishersData, sourceTypesData, metadataTypesData] = await Promise.all([
                    getAllAuthors(),
                    getAllPublishers(),
                    getAllSourceTypes(),
                    getAllMetadataTypes()
                ]);
                setAuthors(authorsData);
                setPublishers(publishersData);
                // Filter out 'other' from source types
                setSourceTypes(sourceTypesData.filter(st => st.source_type !== 'other'));
                setAllMetadataTypes(metadataTypesData);
            } catch (error) {
                console.error('Error loading authors/publishers/source types/metadata types:', error);
            }
        };
        loadData();
    }, []);

    // Re-match custom fields when allMetadataTypes becomes available
    useEffect(() => {
        if (allMetadataTypes.length > 0 && customMetadataFields.length > 0) {
            const updatedFields = customMetadataFields.map(field => {
                // Only update if it's in free mode and we can find a match
                if (field.mode === 'free' && field.key) {
                    const matchingType = allMetadataTypes.find(mt => mt.field_name === field.key);
                    if (matchingType) {
                        return {
                            ...field,
                            mode: 'select' as const,
                            metadataType: matchingType
                        };
                    }
                }
                return field;
            });
            
            // Only update if something changed
            const hasChanges = updatedFields.some((field, index) => 
                field.mode !== customMetadataFields[index].mode || 
                field.metadataType?.id !== customMetadataFields[index].metadataType?.id
            );
            
            if (hasChanges) {
                setCustomMetadataFields(updatedFields);
            }
        }
    }, [allMetadataTypes, customMetadataFields]);

    // Load metadata fields when source type changes or on mount for edit mode
    useEffect(() => {
        const loadMetadataFields = async () => {
            if (!formData.source_type) return;
            setLoading(true);
            try {
                // Add delay to show spinner
                await new Promise(resolve => setTimeout(resolve, 300));
                
                const fields = await getMetadataBySourceType(formData.source_type);
                setMetadataFields(fields);
                
                if (mode === 'edit') {
                    // In edit mode, preserve existing metadata values
                    const existingMetadata = formData.metadata || {};
                    const predefinedFieldNames = fields.map(f => f.field_name);
                    
                    // Separate predefined and custom fields
                    const customMetadata: Record<string, any> = {};
                    Object.keys(existingMetadata).forEach(key => {
                        if (!predefinedFieldNames.includes(key) && key !== 'ingest_source') {
                            customMetadata[key] = existingMetadata[key];
                        }
                    });
                    
                    // Try to match custom fields with metadata types (only if allMetadataTypes is loaded)
                    const preservedCustomFields = Object.keys(customMetadata).map(key => {
                        const matchingType = allMetadataTypes.length > 0 
                            ? allMetadataTypes.find(mt => mt.field_name === key)
                            : null;
                        return {
                            mode: matchingType ? 'select' as const : 'free' as const,
                            key,
                            value: String(customMetadata[key] || ''),
                            metadataType: matchingType || null
                        };
                    });
                    
                    setCustomMetadataFields(preservedCustomFields);
                    
                    // Extract ingest_source from metadata if it exists
                    const ingestSource = existingMetadata.ingest_source || formData.ingest_source;
                    setFormData(prev => ({
                        ...prev,
                        ingest_source: ingestSource
                    }));
                    
                    // Set author search term if author exists in metadata
                    if (existingMetadata.author && typeof existingMetadata.author === 'string') {
                        setAuthorSearchTerm(existingMetadata.author);
                    }
                } else {
                    // In create mode, reset metadata when source type changes, but preserve custom fields
                    const customMetadata: Record<string, any> = {};
                    const predefinedFieldNames = fields.map(f => f.field_name);
                    
                    // Preserve custom metadata fields (those not in the predefined list)
                    Object.keys(formData.metadata).forEach(key => {
                        if (!predefinedFieldNames.includes(key) && key !== 'ingest_source') {
                            customMetadata[key] = formData.metadata[key];
                        }
                    });
                    
                    const newMetadata: Record<string, any> = { ...customMetadata };
                    
                    // Pre-populate ingest_source in metadata if it's a default field
                    const ingestSourceField = fields.find(f => f.field_name === 'ingest_source');
                    if (ingestSourceField && ingestSourceField.is_default && formData.ingest_source) {
                        newMetadata.ingest_source = formData.ingest_source;
                    }
                    
                    // Try to match custom fields with metadata types (only if allMetadataTypes is loaded)
                    const preservedCustomFields = Object.keys(customMetadata).map(key => {
                        const matchingType = allMetadataTypes.length > 0 
                            ? allMetadataTypes.find(mt => mt.field_name === key)
                            : null;
                        return {
                            mode: matchingType ? 'select' as const : 'free' as const,
                            key,
                            value: String(customMetadata[key] || ''),
                            metadataType: matchingType || null
                        };
                    });
                    
                    setCustomMetadataFields(preservedCustomFields);
                    
                    setFormData(prev => ({
                        ...prev,
                        metadata: newMetadata,
                        ingest_source: undefined
                    }));
                }
            } catch (error) {
                console.error('Error loading metadata fields:', error);
            } finally {
                setLoading(false);
            }
        };
        loadMetadataFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.source_type, mode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Add new author to database if it's new and hasn't been added yet
        if (isNewAuthor && authorSearchTerm.trim() && !isAddingNewAuthor) {
            await handleAddNewAuthor(authorSearchTerm);
        }
        
        // Ensure ingest_source is in metadata if it exists
        const submitData = {
            ...formData,
            metadata: {
                ...formData.metadata,
                ...(formData.ingest_source && { ingest_source: formData.ingest_source })
            }
        };
        
        onSubmit(submitData);
    };

    const updateMetadataField = (fieldName: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                [fieldName]: value
            }
        }));
    };

    const filteredAuthors = authors.filter(author =>
        author.toLowerCase().includes(authorSearchTerm.toLowerCase())
    );

    // Check if current input is a new author (not in the list)
    const isNewAuthor = authorSearchTerm.trim() && 
        authorSearchTerm.trim().toLowerCase() !== '' &&
        !authors.some(a => a.toLowerCase() === authorSearchTerm.trim().toLowerCase());

    // Function to add new author to the database
    const handleAddNewAuthor = async (authorName: string) => {
        const trimmedName = authorName.trim();
        if (!trimmedName || authors.some(a => a.toLowerCase() === trimmedName.toLowerCase())) {
            return; // Already exists or empty
        }

        try {
            setIsAddingNewAuthor(true);
            await addAuthor(trimmedName);
            // Reload authors list
            const updatedAuthors = await getAllAuthors();
            setAuthors(updatedAuthors);
        } catch (error) {
            console.error('Error adding new author:', error);
            // Don't throw - allow the form to still work
        } finally {
            setIsAddingNewAuthor(false);
        }
    };

    const handleAddTag = () => {
        const tag = newTag.trim();
        if (tag && !formData.filter_tags.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                filter_tags: [...prev.filter_tags, tag]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            filter_tags: prev.filter_tags.filter(tag => tag !== tagToRemove)
        }));
    };

    // Reusable function to render field input based on type
    const renderFieldInput = (
        fieldType: 'text' | 'number' | 'date' | 'enum' | 'select',
        value: string | number | null | undefined,
        onChange: (value: any) => void,
        options?: {
            enumOptions?: (string | Record<string, any>)[] | null;
            placeholder?: string;
            required?: boolean;
            className?: string;
            style?: React.CSSProperties;
            fieldName?: string; // For number fields to determine null vs empty string
        }
    ) => {
        const {
            enumOptions,
            placeholder,
            required = false,
            className = 'cms-modal-input',
            style,
            fieldName
        } = options || {};

        // Normalize value for display (convert null/undefined to empty string)
        const displayValue = value === null || value === undefined ? '' : String(value);

        switch (fieldType) {
            case 'number':
                return (
                    <input
                        type="number"
                        required={required}
                        value={displayValue}
                        onChange={(e) => {
                            // Store null for empty number fields, not empty string
                            const numValue = e.target.value && e.target.value.trim() !== '' 
                                ? parseInt(e.target.value) 
                                : null;
                            onChange(numValue);
                        }}
                        className={className}
                        style={style}
                        placeholder={placeholder}
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        required={required}
                        value={displayValue}
                        onChange={(e) => onChange(e.target.value)}
                        className={className}
                        style={style}
                        placeholder={placeholder}
                    />
                );
            case 'enum':
            case 'select':
                // Filter enumOptions to only include string values (exclude checklist objects)
                const stringOptions = enumOptions?.filter((opt): opt is string => typeof opt === 'string') || [];
                return (
                    <select
                        value={displayValue}
                        required={required}
                        onChange={(e) => onChange(e.target.value)}
                        className={className}
                        style={style}
                    >
                        <option value="" disabled={required}>
                            {placeholder || 'Select option'}
                        </option>
                        {stringOptions.map((option: string) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                );
            default: // text
                return (
                    <input
                        type="text"
                        required={required}
                        value={displayValue}
                        onChange={(e) => onChange(e.target.value)}
                        className={className}
                        style={style}
                        placeholder={placeholder}
                    />
                );
        }
    };

    const renderMetadataField = (field: any) => {
        const fieldValue = formData.metadata[field.field_name] || '';

        if (field.field_name === 'author') {
            const requirement = getFieldRequirement('author');
            const isRequired = requirement === 'compulsory';
            return (
                <div key={field.id} className="cms-modal-form-group">
                    <label className="cms-modal-label">
                        Author {isRequired ? '*' : requirement === 'suggested' ? '' : ''}
                    </label>
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'relative' }} data-author-input>
                            <input
                                type="text"
                                required={isRequired}
                                value={authorSearchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setAuthorSearchTerm(value);
                                    updateMetadataField('author', value);
                                    setAuthorDropdownOpen(value.length > 0 && filteredAuthors.length > 0);
                                    setSelectedAuthorIndex(-1); // Reset selection when typing
                                }}
                                onFocus={() => {
                                    if (authorSearchTerm && filteredAuthors.length > 0) {
                                        setAuthorDropdownOpen(true);
                                    }
                                }}
                                onBlur={(e) => {
                                    // Delay closing to allow click on dropdown items
                                    setTimeout(() => {
                                        setAuthorDropdownOpen(false);
                                        setSelectedAuthorIndex(-1);
                                        // Add new author to database if it's new
                                        if (isNewAuthor && authorSearchTerm.trim()) {
                                            handleAddNewAuthor(authorSearchTerm);
                                        }
                                    }, 200);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        if (filteredAuthors.length > 0) {
                                            setAuthorDropdownOpen(true);
                                            setSelectedAuthorIndex(prev => 
                                                prev < filteredAuthors.length - 1 ? prev + 1 : prev
                                            );
                                        }
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        if (filteredAuthors.length > 0) {
                                            setSelectedAuthorIndex(prev => prev > 0 ? prev - 1 : -1);
                                        }
                                    } else if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (selectedAuthorIndex >= 0 && selectedAuthorIndex < filteredAuthors.length) {
                                            // Select the highlighted author
                                            const selectedAuthor = filteredAuthors[selectedAuthorIndex];
                                            setAuthorSearchTerm(selectedAuthor);
                                            updateMetadataField('author', selectedAuthor);
                                            setAuthorDropdownOpen(false);
                                            setSelectedAuthorIndex(-1);
                                            e.currentTarget.blur();
                                        } else {
                                            // Save the current typed value and add if new
                                            updateMetadataField('author', authorSearchTerm);
                                            if (isNewAuthor && authorSearchTerm.trim()) {
                                                handleAddNewAuthor(authorSearchTerm);
                                            }
                                            setAuthorDropdownOpen(false);
                                            setSelectedAuthorIndex(-1);
                                            e.currentTarget.blur();
                                        }
                                    } else if (e.key === 'Escape') {
                                        setAuthorDropdownOpen(false);
                                        setSelectedAuthorIndex(-1);
                                    }
                                }}
                                className="cms-modal-input"
                                placeholder="Author's name..."
                                style={{
                                    paddingRight: isNewAuthor ? '90px' : '12px'
                                }}
                            />
                            {isNewAuthor && (
                                <div style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    pointerEvents: 'none',
                                    color: '#10b981',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    {isAddingNewAuthor ? (
                                        <>
                                            <div style={{
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #10b981',
                                                borderTopColor: 'transparent',
                                                borderRadius: '50%',
                                                animation: 'author-spin 0.6s linear infinite'
                                            }}></div>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontSize: '1rem' }}>+</span>
                                            <span>New Author</span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {authorDropdownOpen && filteredAuthors.length > 0 && (
                            <div 
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                                onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                            >
                                {filteredAuthors.map((author, index) => (
                                    <div
                                        key={index}
                                        ref={(el) => {
                                            // Scroll into view when selected
                                            if (index === selectedAuthorIndex && el) {
                                                el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setAuthorSearchTerm(author);
                                            updateMetadataField('author', author);
                                            setAuthorDropdownOpen(false);
                                            setSelectedAuthorIndex(-1);
                                        }}
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: index === selectedAuthorIndex ? '#e3f2fd' : 'white'
                                        }}
                                        onMouseEnter={() => setSelectedAuthorIndex(index)}
                                        onMouseLeave={() => {
                                            // Don't reset on mouse leave to keep keyboard selection visible
                                        }}
                                    >
                                        {author}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (field.field_name === 'publisher') {
            const requirement = getFieldRequirement('publisher');
            const isRequired = requirement === 'compulsory';
            return (
                <div key={field.id} className="cms-modal-form-group">
                    <label className="cms-modal-label">
                        Publisher {isRequired ? '*' : requirement === 'suggested' ? '' : ''}
                    </label>
                    <select
                        value={fieldValue}
                        required={isRequired}
                        onChange={(e) => updateMetadataField('publisher', e.target.value)}
                        className="cms-modal-select"
                    >
                        <option value="" disabled={isRequired}>Select Publisher</option>
                        {publishers.map((publisher, index) => {
                            const publisherValue = publisher.shortName 
                                ? `${publisher.name} (${publisher.shortName})` 
                                : publisher.name;
                            return (
                                <option key={index} value={publisherValue}>
                                    {publisherValue}
                                </option>
                            );
                        })}
                    </select>
                </div>
            );
        }

        if (field.field_name === 'language') {
            const requirement = getFieldRequirement('language');
            const isRequired = requirement === 'compulsory';
            return (
                <div key={field.id} className="cms-modal-form-group">
                    <label className="cms-modal-label">
                        Language {isRequired ? '*' : requirement === 'suggested' ? '' : ''}
                    </label>
                    <select
                        value={fieldValue}
                        required={isRequired}
                        onChange={(e) => updateMetadataField('language', e.target.value)}
                        className="cms-modal-select"
                    >
                        <option value="" disabled={isRequired}>Select Language</option>
                        <option value="English">English</option>
                        <option value="Gujarati">Gujarati</option>
                        <option value="Hindi">Hindi</option>
                    </select>
                </div>
            );
        }

        if (field.field_name === 'ingest_source') {
            const requirement = getFieldRequirement('ingest_source');
            const isRequired = requirement === 'compulsory';
            return (
                <div key={field.id} className="cms-modal-form-group">
                    <label className="cms-modal-label">
                        Ingest Source {isRequired ? '*' : requirement === 'suggested' ? '' : ''}
                    </label>
                    <select
                        value={formData.ingest_source || fieldValue || ''}
                        required={isRequired}
                        onChange={(e) => {
                            setFormData(prev => ({ ...prev, ingest_source: e.target.value }));
                            updateMetadataField('ingest_source', e.target.value);
                        }}
                        className="cms-modal-select"
                    >
                        <option value="" disabled={isRequired}>Select Ingest Source</option>
                        {field.enum_options?.map((option: string) => (
                            <option key={option} value={option}>
                                {INGEST_SOURCE_LABELS[option as keyof typeof INGEST_SOURCE_LABELS] || option}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        const requirement = getFieldRequirement(field.field_name);
        const isRequired = requirement === 'compulsory';
        const fieldLabel = field.field_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        const labelSuffix = isRequired ? '*' : requirement === 'suggested' ? '' : '';

        // Use reusable renderFieldInput for standard field types
        const inputElement = renderFieldInput(
            field.field_type,
            fieldValue,
            (value) => updateMetadataField(field.field_name, value),
            {
                enumOptions: field.enum_options,
                required: isRequired,
                className: field.field_type === 'enum' || field.field_type === 'select' 
                    ? 'cms-modal-select' 
                    : 'cms-modal-input',
                placeholder: field.field_type === 'enum' || field.field_type === 'select' 
                    ? `Select ${fieldLabel}` 
                    : undefined,
                fieldName: field.field_name
            }
        );

        if (!inputElement) return null;

        return (
            <div key={field.id} className="cms-modal-form-group">
                <label className="cms-modal-label">
                    {fieldLabel} {labelSuffix}
                </label>
                {inputElement}
            </div>
        );
    };

    return (
        <div className="cms-modal-overlay" onClick={onClose}>
            <div className="cms-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="cms-modal-header">
                    <h2 className="cms-modal-title">
                        {mode === 'create' ? 'Create Content Source' : 'Edit Content Source'}
                    </h2>
                    <div className="cms-modal-header-actions">
                        <button
                            type="submit"
                            form="content-source-form"
                            className="cms-modal-button cms-modal-button-submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting 
                                ? (mode === 'create' ? 'Creating...' : 'Saving...') 
                                : (mode === 'create' ? 'Create & Open' : 'Save Changes')
                            }
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="cms-modal-button-close"
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>
                <form 
                    id="content-source-form" 
                    onSubmit={handleSubmit} 
                    className="cms-modal-form"
                    onKeyDown={(e) => {
                        // Prevent Enter from submitting the form unless it's from a submit button
                        if (e.key === 'Enter' && e.target instanceof HTMLElement) {
                            // Allow Enter in textareas and in the author field (which has its own Enter handler)
                            if (e.target.tagName === 'TEXTAREA' || e.target.closest('[data-author-input]')) {
                                return; // Let the field handle it
                            }
                            // Allow Enter if it's from a submit button
                            if ((e.target instanceof HTMLButtonElement && e.target.type === 'submit') || 
                                e.target.closest('button[type="submit"]')) {
                                return; // Let the button handle it
                            }
                            // Prevent form submission for Enter in other inputs
                            e.preventDefault();
                        }
                    }}
                >
                    {error && (
                        <div className="cms-modal-form-group" style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#dc2626',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Sarangpur Path Section - Admin Only */}
                    {isAdmin && (
                        <div style={{ paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                            <h3 className="cms-modal-section-title">PDF Path (Admin Only)</h3>
                            
                            <div className="cms-modal-form-grid">
                                <div className="cms-modal-form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label className="cms-modal-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        Sarangpur Path
                                        <Tooltip.Provider>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <Info className="cms-modal-info-icon" size={16} />
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content
                                                        className="tooltip-content"
                                                        side="right"
                                                        sideOffset={5}
                                                    >
                                                        Windows network path to the PDF file (e.g., \\sarangpur.net\Data\...)
                                                        <Tooltip.Arrow className="tooltip-arrow" />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </Tooltip.Provider>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sarangpur_path || ''}
                                        onChange={(e) => setFormData({ ...formData, sarangpur_path: e.target.value })}
                                        className="cms-modal-input"
                                        placeholder="\\sarangpur.net\Data\Prakalps - Sankalans - Projects\BAPS AI Data Collection\..."
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="cms-modal-form-grid">
                        <div className="cms-modal-form-group">
                            <label className="cms-modal-label">
                                Title *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="cms-modal-input"
                            />
                        </div>
                        
                        <div className="cms-modal-form-group">
                            <label className="cms-modal-label">
                                Source Type
                            </label>
                            <select
                                value={formData.source_type}
                                onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                                className="cms-modal-select"
                            >
                                <option value="">Select Source Type</option>
                                {sourceTypes.map((sourceType) => (
                                    <option key={sourceType.id} value={sourceType.source_type}>
                                        {sourceType.source_type.charAt(0).toUpperCase() + sourceType.source_type.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ position: 'relative', minHeight: '100px', gridColumn: '1 / -1' }}>
                            {loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10,
                                    borderRadius: '4px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                        <span style={{ fontSize: '14px', color: '#666' }}>Loading metadata fields...</span>
                                    </div>
                                </div>
                            )}
                            <div style={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.2s', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                {metadataFields.map(field => renderMetadataField(field))}
                            </div>
                        </div>
                        
                        {/* Custom Metadata Fields */}
                        <div className="cms-modal-form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', gridColumn: '1 / -1' }}>
                            <label className="cms-modal-label" style={{ marginBottom: '12px', fontWeight: '600' }}>
                                Additional Metadata Fields
                            </label>
                            {customMetadataFields.map((customField, index) => {
                                // Get predefined field names to exclude from dropdown
                                const predefinedFieldNames = metadataFields.map(f => f.field_name);
                                // Filter out predefined fields and get available metadata types
                                const availableMetadataTypes = allMetadataTypes.filter(
                                    mt => !predefinedFieldNames.includes(mt.field_name)
                                );

                                const renderValueInput = () => {
                                    if (customField.mode === 'select' && customField.metadataType) {
                                        const fieldType = customField.metadataType.field_type;
                                        const fieldValue = customField.value || '';

                                        // Use reusable renderFieldInput for metadata type fields
                                        return renderFieldInput(
                                            fieldType,
                                            fieldValue,
                                            (value) => {
                                                const updated = [...customMetadataFields];
                                                // Store string value in state for display, but proper type in metadata
                                                updated[index].value = value !== null && value !== undefined ? String(value) : '';
                                                setCustomMetadataFields(updated);
                                                
                                                const newMetadata = { ...formData.metadata };
                                                if (customField.key) {
                                                    newMetadata[customField.key] = value;
                                                }
                                                setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                            },
                                            {
                                                enumOptions: customField.metadataType.enum_options,
                                                className: fieldType === 'enum' || fieldType === 'select' 
                                                    ? 'cms-modal-select' 
                                                    : 'cms-modal-input',
                                                placeholder: fieldType === 'enum' || fieldType === 'select' 
                                                    ? `Select ${customField.metadataType.field_name}` 
                                                    : undefined,
                                                style: { flex: 1 },
                                                fieldName: customField.key
                                            }
                                        );
                                    } else {
                                        // Custom field text mode - use reusable function
                                        return renderFieldInput(
                                            'text',
                                            customField.value,
                                            (value) => {
                                                const updated = [...customMetadataFields];
                                                updated[index].value = value;
                                                setCustomMetadataFields(updated);
                                                const newMetadata = { ...formData.metadata };
                                                if (customField.key) {
                                                    newMetadata[customField.key] = value;
                                                }
                                                setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                            },
                                            {
                                                placeholder: 'Value',
                                                className: 'cms-modal-input',
                                                style: { flex: 1 }
                                            }
                                        );
                                    }
                                };

                                return (
                                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                        {/* Mode selector */}
                                        <select
                                            value={customField.mode}
                                            onChange={(e) => {
                                                const updated = [...customMetadataFields];
                                                const oldKey = updated[index].key;
                                                updated[index].mode = e.target.value as 'select' | 'free';
                                                if (e.target.value === 'free') {
                                                    updated[index].metadataType = null;
                                                    updated[index].key = '';
                                                } else {
                                                    updated[index].key = '';
                                                    updated[index].value = '';
                                                }
                                                setCustomMetadataFields(updated);
                                                
                                                // Update metadata to remove old key
                                                const newMetadata = { ...formData.metadata };
                                                if (oldKey) {
                                                    delete newMetadata[oldKey];
                                                }
                                                setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                            }}
                                            className="cms-modal-select"
                                            style={{ width: '120px' }}
                                        >
                                            <option value="select">From DB</option>
                                            <option value="free">Custom Text</option>
                                        </select>

                                        {/* Field name input - different based on mode */}
                                        {customField.mode === 'select' ? (
                                            <select
                                                value={customField.metadataType?.field_name || ''}
                                                onChange={(e) => {
                                                    const selectedType = e.target.value 
                                                        ? allMetadataTypes.find(mt => mt.field_name === e.target.value)
                                                        : null;
                                                    const updated = [...customMetadataFields];
                                                    const oldKey = updated[index].key;
                                                    
                                                    if (selectedType) {
                                                        updated[index].metadataType = selectedType;
                                                        updated[index].key = selectedType.field_name;
                                                        updated[index].value = '';
                                                    } else {
                                                        // Empty option selected - clear the field
                                                        updated[index].metadataType = null;
                                                        updated[index].key = '';
                                                        updated[index].value = '';
                                                    }
                                                    
                                                    setCustomMetadataFields(updated);
                                                    
                                                    // Update metadata - always delete old key, add new one only if selectedType exists
                                                    const newMetadata = { ...formData.metadata };
                                                    if (oldKey) {
                                                        delete newMetadata[oldKey];
                                                    }
                                                    if (selectedType) {
                                                        newMetadata[selectedType.field_name] = '';
                                                    }
                                                    setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                                }}
                                                className="cms-modal-select"
                                                style={{ flex: 1 }}
                                            >
                                                <option value="">Select metadata type...</option>
                                                {availableMetadataTypes.map((mt) => (
                                                    <option key={mt.id} value={mt.field_name}>
                                                        {mt.field_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} ({mt.field_type})
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder="Field name"
                                                value={customField.key}
                                                onChange={(e) => {
                                                    const updated = [...customMetadataFields];
                                                    updated[index].key = e.target.value;
                                                    setCustomMetadataFields(updated);
                                                    // Update metadata
                                                    const newMetadata = { ...formData.metadata };
                                                    if (customField.key) {
                                                        delete newMetadata[customField.key];
                                                    }
                                                    if (e.target.value) {
                                                        newMetadata[e.target.value] = customField.value;
                                                    }
                                                    setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                                }}
                                                className="cms-modal-input"
                                                style={{ flex: 1 }}
                                            />
                                        )}

                                        {/* Value input - rendered based on mode and type */}
                                        {renderValueInput()}

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = [...customMetadataFields];
                                                const removedField = updated[index];
                                                updated.splice(index, 1);
                                                setCustomMetadataFields(updated);
                                                // Remove from metadata
                                                const newMetadata = { ...formData.metadata };
                                                if (removedField.key) {
                                                    delete newMetadata[removedField.key];
                                                }
                                                setFormData(prev => ({ ...prev, metadata: newMetadata }));
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                backgroundColor: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomMetadataFields([...customMetadataFields, { 
                                            mode: 'select', 
                                            key: '', 
                                            value: '',
                                            metadataType: null
                                        }]);
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    + Add Metadata
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomMetadataFields([...customMetadataFields, { 
                                            mode: 'free', 
                                            key: '', 
                                            value: '',
                                            metadataType: null
                                        }]);
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    + Add Custom Field
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Visibility & Organization Settings Section */}
                    <div className="cms-modal-visibility-section">
                        <h3 className="cms-modal-section-title">Visibility & Organization Settings</h3>
                        
                        <div className="cms-modal-form-grid">
                            <div className="cms-modal-form-group">
                                <label className="cms-modal-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Visibility
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Controls who can view this content.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                <select
                                    value={formData.visibility}
                                    onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                                    className="cms-modal-select"
                                >
                                    {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="cms-modal-form-group">
                                <label className="cms-modal-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Ref Visibility
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Controls if the AI will display this reference source.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                <select
                                    value={formData.ref_visibility}
                                    onChange={(e) => setFormData({ ...formData, ref_visibility: e.target.value })}
                                    className="cms-modal-select"
                                >
                                    {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="cms-modal-form-group" style={{ gridColumn: '1 / -1' }}>
                                <label className="cms-modal-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Filter Tags
                                    <Tooltip.Provider>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Info className="cms-modal-info-icon" size={16} />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    className="tooltip-content"
                                                    side="right"
                                                    sideOffset={5}
                                                >
                                                    Tags used for internal filtering for controversial or sensitive topics.
                                                    <Tooltip.Arrow className="tooltip-arrow" />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                    {formData.filter_tags.map((tag, index) => (
                                        <span key={index} style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 8px',
                                            backgroundColor: '#e8eaed',
                                            borderRadius: '4px',
                                            fontSize: '0.875rem'
                                        }}>
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    fontSize: '0.875rem',
                                                    color: '#666'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Add tag..."
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddTag();
                                            }
                                        }}
                                        className="cms-modal-input"
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddTag}
                                        className="cms-modal-button"
                                        style={{ padding: '8px 16px' }}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContentSourceModal;

